/**
 * Entrare, uscire, cambiare la propria parola d'ordine.
 *
 * **Il gettone vive nella scheda, non nel calcolatore.** `sessionStorage` e non
 * `localStorage`: le postazioni sono condivise, e un gettone che sopravvive
 * alla chiusura del navigatore e' il turno di qualcun altro sotto il nome di
 * chi e' entrato la volta prima. Chiudere la scheda deve chiudere il lavoro.
 *
 * **Il rifiuto d'accesso e' sempre lo stesso**, quale che sia la causa. Il
 * servizio e' fatto cosi' di proposito — distinguere «utenza inesistente» da
 * «parola sbagliata» direbbe a chiunque quali identificativi esistono — e la
 * pagina non deve disfare quella scelta aggiungendo una spiegazione gentile.
 */
import { ask, get, send, useToken } from './api'

const WHERE = 'gridfly.session'

export interface Session {
  token: string
  expires_in: number
  /** Se la parola d'ordine consegnata va ancora cambiata: finche' non lo e',
   *  la conosce anche chi ha aperto l'utenza. */
  must_change_password: boolean
}

interface StoredSession {
  token: string
  must_change_password: boolean
}

// Le azioni, coi nomi che usa il servizio. Sono i nomi delle *cose che si
// fanno*, non dei percorsi: un percorso cambia, il fatto che esportare una
// consegna sia facolta' del supervisore no.
export const PACKAGE_UPLOAD = 'package.upload'
export const REVIEW_QUEUE = 'review.queue'
export const EXPORT_COMPOSE = 'export.compose'
export const EXPORT_VALIDATE = 'export.validate'
export const EXPORT_SEND = 'export.send'
export const EXPORT_OUTCOME = 'export.outcome'
export const EXPORT_RECEIPT_READ = 'export.receipt.read'
export const INSPECTION_POOL_READ = 'inspection.pool.read'
export const FINDING_MANUAL = 'finding.manual'
export const QUALITY_PROGRESS_READ = 'quality.progress.read'
export const QUALITY_SAMPLE = 'quality.sample'
export const QUALITY_REVIEW_APPEND = 'quality.review.append'
export const QUALITY_SEAL = 'quality.seal'
export const REVIEW_FREEZE = 'review.freeze'
export const UPLOAD_RECEIPT_READ = 'upload.receipt.read'
export const INGEST_READ = 'ingest.read'
export const INGEST_RETRY = 'ingest.retry'
export const USER_MANAGE = 'user.manage'
export const MODEL_MANAGE = 'model.manage'
export const SETTINGS_MANAGE = 'settings.manage'
export const CATALOG_IMPORT = 'catalog.import'
export const AUDIT_READ = 'audit.read'

/** Chi sta lavorando e che cosa gli e' permesso. */
export interface Whoami {
  user_id: string
  login: string
  display_name: string
  role: string
  must_change_password?: boolean
  /** Le azioni permesse: arrivano dal servizio, non si deducono dal ruolo. */
  may: string[]
}

function readStored(): StoredSession | null {
  const found = window.sessionStorage.getItem(WHERE)
  if (found === null) return null
  try {
    const parsed: unknown = JSON.parse(found)
    if (typeof parsed === 'object' && parsed !== null && 'token' in parsed) {
      const token = String((parsed as { token: unknown }).token)
      const must = Boolean((parsed as { must_change_password?: unknown }).must_change_password)
      return { token, must_change_password: must }
    }
  } catch {
    // Compatibilita' con il vecchio formato: solo il gettone in chiaro.
    return { token: found, must_change_password: false }
  }
  return { token: found, must_change_password: false }
}

function store(session: StoredSession): void {
  window.sessionStorage.setItem(WHERE, JSON.stringify(session))
}

/** Rimette in uso il gettone di questa scheda, se c'e'. */
export function resume(): string | null {
  const found = readStored()
  useToken(found?.token ?? null)
  return found?.token ?? null
}

export function needsPasswordChange(): boolean {
  return readStored()?.must_change_password ?? false
}

export async function enter(login: string, password: string): Promise<Session> {
  const session = await ask<Session>('/auth/login', { login, password })
  store({
    token: session.token,
    must_change_password: session.must_change_password,
  })
  useToken(session.token)
  return session
}

/** Dimentica il gettone qui. Si chiama anche quando il servizio risponde 401:
 *  una sessione scaduta deve riportare all'ingresso, non a una pagina muta. */
export function forget(): void {
  window.sessionStorage.removeItem(WHERE)
  useToken(null)
}

export async function leave(): Promise<void> {
  try {
    await send<{ status: string }>('/auth/logout', {}, crypto.randomUUID())
  } finally {
    // Il gettone si scorda comunque: se la chiamata non arriva, restare
    // «dentro» in una pagina che crede di essere uscita e' peggio di una
    // sessione che scade da sola al servizio.
    forget()
  }
}

/** Cambia la parola d'ordine. Il servizio chiude tutte le sessioni, questa
 *  compresa: chi cambia deve rientrare, ed e' il punto del cambio. */
export function change(currentPassword: string, newPassword: string): Promise<unknown> {
  return ask('/auth/password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
}

export function whoami(): Promise<Whoami> {
  return get<Whoami>('/auth/me')
}
